import { useState } from "react";

import { BasicModal, Button, Textfield } from "@similarweb/ui-components";

import type { ICategory } from "app/common/services/categoryService.types";
import { Disclaimer } from "app/components/React/Banner/Disclaimer";
import { translate } from "app/filters/i18nFilter";

import { useBuilderContext } from "../../builderContext";
import { useMarketName } from "../../CustomHooks/useMarketName";

import { ButtonsContainer, InputContainer, Subtitle, Title, DisclaimerWrapper } from "./StyledComponent";

interface ICopy {
    title: string;
    banner?: string;
}

interface ISaveModal {
    isOpen: boolean;
    onDone: (industryName: string) => void;
    onCloseModal: VoidFunction;
    category: ICategory;
    textKeys: ICopy;
}

const SaveModal = ({ isOpen, onDone, onCloseModal, category, textKeys }: ISaveModal) => {
    const { title, banner } = textKeys;
    const { marketName, onUpdateMarketName, hasErrorWithName, nameErrorMessage } = useMarketName(category);
    const [isLoading, setIsLoading] = useState(false);
    const [allowSave, setAllowSave] = useState(Boolean(category));
    const { showSegmentsDisclaimer } = useBuilderContext();

    const onDoneClicked = () => {
        setIsLoading(true);
        setAllowSave(false);
        onDone(marketName);
    };

    const onInputChange = (name) => {
        onUpdateMarketName(name);
        setAllowSave(true);
    };

    return (
        <BasicModal
            isOpen={isOpen}
            onRequestClose={onCloseModal}
            closeIconConfig={{ show: false }}
            dataAutomation="SaveModal"
            customStyles={{
                content: {
                    width: "448px",
                },
            }}
        >
            <Title>{translate(title)}</Title>
            <Subtitle>{translate("research.marketbuilder.modal.subtitle")}</Subtitle>
            <InputContainer data-automation="custom-industry-name-input">
                <Textfield
                    error={hasErrorWithName}
                    errorMessage={nameErrorMessage}
                    onChange={onInputChange}
                    placeholder={translate("industry.research.market.builder.create.name.input")}
                    defaultValue={marketName}
                />

                {showSegmentsDisclaimer || banner ? (
                    <Disclaimer
                        disclaimerText={translate(banner ?? "website.research.market.builder.segments.disclaimer")}
                        icon={"alerts"}
                        ContentContainer={DisclaimerWrapper}
                    />
                ) : null}
            </InputContainer>
            <ButtonsContainer>
                <Button
                    type="flat"
                    onClick={onCloseModal}
                >
                    {translate("research.marketbuilder.modal.button.back")}
                </Button>
                <span data-automation="custom-industry-done-button">
                    <Button
                        type="primary"
                        isDisabled={hasErrorWithName || !allowSave}
                        onClick={onDoneClicked}
                        isLoading={isLoading}
                    >
                        {translate("research.marketbuilder.modal.button.done")}
                    </Button>
                </span>
            </ButtonsContainer>
        </BasicModal>
    );
};

export default SaveModal;
